import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, startOfWeek, endOfWeek, eachDayOfInterval, getWeek, isWeekend, addWeeks, subWeeks } from 'date-fns';
import { exportProjectPlan, exportProjectPlanPDF } from '../shared/ExcelExport';
import { API, AVAILABILITY_TYPES, getAvailabilityTypeInfo, formatDateDDMMYYYY, getWeekNumber } from './utils/helpers';

// Import sub-components
import {
  PlanSelector,
  PlanNavigation,
  PlanHeader,
  UnsavedChangesWarning,
  EmployeeGrid,
  CreatePlanDialog,
  EditPlanDialog,
  AddEmployeeDialog,
  BulkAddEmployeeDialog,
  EditEmployeeDialog,
  CustomHolidayDialog,
  UnsavedChangesDialog,
  CopyMultiWeeksDialog,
  ExportDialog,
  SaveTemplateDialog,
  LoadTemplateDialog,
  BulkHoursDialog
} from './project-planning';

const ProjectPlanningTab = ({ projects, employees, settings }) => {
  const { t } = useTranslation();
  // === STATE MANAGEMENT ===
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editPlanDialogOpen, setEditPlanDialogOpen] = useState(false);
  const [addEmployeeDialogOpen, setAddEmployeeDialogOpen] = useState(false);
  const [editEmployeeDialogOpen, setEditEmployeeDialogOpen] = useState(false);
  const [customHolidayDialogOpen, setCustomHolidayDialogOpen] = useState(false);
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [copyMultiWeeksDialogOpen, setCopyMultiWeeksDialogOpen] = useState(false);
  const [copyMultiWeeksForm, setCopyMultiWeeksForm] = useState({ targetWeeks: 1 });
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportRange, setExportRange] = useState({ startDate: '', endDate: '', exportWholePlan: true });
  const [exportLoading, setExportLoading] = useState(false);
  const [bulkHoursDialogOpen, setBulkHoursDialogOpen] = useState(false);
  
  // View navigation state
  const [viewStartDate, setViewStartDate] = useState(null);
  
  // Plan form state
  const [planForm, setPlanForm] = useState({
    project_id: '',
    name: '',
    start_date: '',
    end_date: ''
  });
  
  // Edit plan form state
  const [editPlanForm, setEditPlanForm] = useState({
    name: '',
    start_date: '',
    end_date: ''
  });
  
  // Plan data state
  const [planEntries, setPlanEntries] = useState([]);
  const [originalEntries, setOriginalEntries] = useState([]);
  const [customNonWorkingDays, setCustomNonWorkingDays] = useState([]);
  const [originalCustomDays, setOriginalCustomDays] = useState([]);
  const [polandHolidays, setPolandHolidays] = useState([]);
  const [projectHolidays, setProjectHolidays] = useState([]);
  const [availability, setAvailability] = useState([]);
  
  // Copy state
  const [copiedData, setCopiedData] = useState(null);
  
  // Employee order state
  const [employeeOrder, setEmployeeOrder] = useState([]);
  const [originalEmployeeOrder, setOriginalEmployeeOrder] = useState([]);
  
  // Employee forms
  const [newEmployeeForm, setNewEmployeeForm] = useState({ employee_id: '', role: '' });
  const [editEmployeeForm, setEditEmployeeForm] = useState({ original_id: '', employee_id: '', role: '' });
  
  // Bulk add employees state
  const [bulkAddDialogOpen, setBulkAddDialogOpen] = useState(false);
  const [bulkSelectedEmployees, setBulkSelectedEmployees] = useState([]);
  const [bulkRole, setBulkRole] = useState('');
  
  // Custom holiday form
  const [customHolidayForm, setCustomHolidayForm] = useState({ date: '' });
  
  // Default hours state
  const [defaultWorkHours, setDefaultWorkHours] = useState(10);
  const [defaultTravelHours, setDefaultTravelHours] = useState(0);
  
  // Template state
  const [templates, setTemplates] = useState([]);
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [loadTemplateDialogOpen, setLoadTemplateDialogOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '' });
  
  // Company info for exports
  const [companyInfo, setCompanyInfo] = useState(null);

  // Project roles from settings
  const projectRoles = settings?.project_roles || [];

  // === COMPUTED VALUES ===
  
  // Check for unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!selectedPlan) return false;
    const entriesChanged = JSON.stringify(planEntries) !== JSON.stringify(originalEntries);
    const customDaysChanged = JSON.stringify(customNonWorkingDays) !== JSON.stringify(originalCustomDays);
    const orderChanged = JSON.stringify(employeeOrder) !== JSON.stringify(originalEmployeeOrder);
    return entriesChanged || customDaysChanged || orderChanged;
  }, [selectedPlan, planEntries, originalEntries, customNonWorkingDays, originalCustomDays, employeeOrder, originalEmployeeOrder]);

  // Full plan dates
  const allPlanDates = useMemo(() => {
    if (!selectedPlan) return [];
    const start = new Date(selectedPlan.start_date);
    const end = new Date(selectedPlan.end_date);
    return eachDayOfInterval({ start, end });
  }, [selectedPlan]);

  // Visible dates (4 weeks from viewStartDate)
  const visibleDates = useMemo(() => {
    if (!viewStartDate || !selectedPlan) return [];
    const planStart = new Date(selectedPlan.start_date);
    const planEnd = new Date(selectedPlan.end_date);
    
    let start = viewStartDate < planStart ? planStart : viewStartDate;
    start = start > planEnd ? planEnd : start;
    
    const viewEnd = addWeeks(start, 4);
    const end = viewEnd < planEnd ? viewEnd : planEnd;
    
    if (start > end) return [start];
    return eachDayOfInterval({ start, end });
  }, [viewStartDate, selectedPlan]);

  // Week options for dropdown
  const weekOptions = useMemo(() => {
    if (!selectedPlan) return [];
    const start = new Date(selectedPlan.start_date);
    const end = new Date(selectedPlan.end_date);
    const options = [];
    
    let current = startOfWeek(start, { weekStartsOn: 1 });
    while (current <= end) {
      const weekEnd = endOfWeek(current, { weekStartsOn: 1 });
      const weekNum = getWeekNumber(current);
      options.push({
        value: format(current, 'yyyy-MM-dd'),
        label: `Week ${weekNum} (${formatDateDDMMYYYY(current)} - ${formatDateDDMMYYYY(weekEnd)})`
      });
      current = addWeeks(current, 1);
    }
    return options;
  }, [selectedPlan]);

  const currentWeekValue = useMemo(() => {
    if (!viewStartDate) return '';
    return format(startOfWeek(viewStartDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
  }, [viewStartDate]);

  // Get employees in plan
  const planEmployees = useMemo(() => {
    if (employeeOrder.length > 0) {
      return employeeOrder.map(item => {
        if (item.isSpacer) return item;
        const emp = employees.find(em => em.id === item.id);
        return { ...item, name: emp?.name || item.name || 'Unknown' };
      });
    }
    
    const employeeMap = new Map();
    planEntries.forEach(e => {
      if (!employeeMap.has(e.employee_id)) {
        const emp = employees.find(em => em.id === e.employee_id);
        employeeMap.set(e.employee_id, {
          id: e.employee_id,
          name: emp?.name || e.employee_name || 'Unknown',
          role: e.role || ''
        });
      }
    });
    return Array.from(employeeMap.values());
  }, [employeeOrder, planEntries, employees]);

  // Calculate max weeks available for copy
  const maxCopyWeeks = useMemo(() => {
    if (!viewStartDate || !selectedPlan) return 0;
    const currentWeekStart = startOfWeek(viewStartDate, { weekStartsOn: 1 });
    const planEnd = new Date(selectedPlan.end_date);
    let count = 0;
    let checkDate = addWeeks(currentWeekStart, 1);
    while (checkDate <= planEnd && count < 52) {
      count++;
      checkDate = addWeeks(checkDate, 1);
    }
    return count;
  }, [viewStartDate, selectedPlan]);

  // === DATA FETCHING ===
  
  const fetchPlans = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/manpower-plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get(`${API}/manpower-templates`);
        setTemplates(response.data);
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    };
    
    const fetchCompanyInfo = async () => {
      try {
        const response = await axios.get(`${API}/company/profile`);
        setCompanyInfo(response.data);
      } catch (error) {
        console.error('Error fetching company info:', error);
      }
    };
    
    fetchTemplates();
    fetchCompanyInfo();
  }, []);

  // === NAVIGATION & UNSAVED CHANGES HANDLING ===
  
  const checkUnsavedAndExecute = (action) => {
    if (hasUnsavedChanges) {
      setPendingAction(() => action);
      setUnsavedChangesDialogOpen(true);
    } else {
      action();
    }
  };

  const handleDiscardChanges = () => {
    setUnsavedChangesDialogOpen(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const handleSaveAndContinue = async () => {
    await savePlan();
    setUnsavedChangesDialogOpen(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  // === PLAN OPERATIONS ===
  
  const loadPlan = async (planId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/manpower-plans/${planId}`);
      const plan = response.data;
      setSelectedPlan(plan);
      const entries = plan.entries || [];
      const customDays = plan.custom_non_working_days || [];
      const savedOrder = plan.employee_order || [];
      
      setPlanEntries(entries);
      setOriginalEntries(JSON.parse(JSON.stringify(entries)));
      setCustomNonWorkingDays(customDays);
      setOriginalCustomDays(JSON.parse(JSON.stringify(customDays)));
      setViewStartDate(new Date(plan.start_date));
      
      if (savedOrder.length > 0) {
        setEmployeeOrder(savedOrder);
        setOriginalEmployeeOrder(JSON.parse(JSON.stringify(savedOrder)));
      } else {
        const employeeMap = new Map();
        entries.forEach(e => {
          if (!employeeMap.has(e.employee_id)) {
            employeeMap.set(e.employee_id, {
              id: e.employee_id,
              name: e.employee_name || 'Unknown',
              role: e.role || ''
            });
          }
        });
        const derivedOrder = Array.from(employeeMap.values());
        setEmployeeOrder(derivedOrder);
        setOriginalEmployeeOrder(JSON.parse(JSON.stringify(derivedOrder)));
      }
      
      const year = new Date(plan.start_date).getFullYear();
      const [plHolidays, projHolidays, availRes] = await Promise.all([
        axios.get(`${API}/holidays/poland/${year}`).catch(() => ({ data: [] })),
        plan.project_country ? 
          axios.get(`${API}/holidays/${plan.project_country}/${year}`).catch(() => ({ data: [] })) : 
          Promise.resolve({ data: [] }),
        axios.get(`${API}/availability/calendar?start_date=${plan.start_date.split('T')[0]}&end_date=${plan.end_date.split('T')[0]}`).catch(() => ({ data: [] }))
      ]);
      
      setPolandHolidays(plHolidays.data);
      setProjectHolidays(projHolidays.data);
      setAvailability(availRes.data);
    } catch (error) {
      console.error('Error loading plan:', error);
      toast.error(t('auto.ctr.failedToLoadPlan'));
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = (planId) => {
    if (planId === selectedPlan?.id) return;
    checkUnsavedAndExecute(() => loadPlan(planId));
  };

  const handleCreatePlan = async () => {
    if (!planForm.project_id || !planForm.start_date || !planForm.end_date) {
      toast.error(t('auto.ctr.pleaseFillAllRequiredFields'));
      return;
    }

    try {
      const response = await axios.post(`${API}/manpower-plans`, {
        project_id: planForm.project_id,
        name: planForm.name || null,
        start_date: planForm.start_date,
        end_date: planForm.end_date
      });
      toast.success(t('auto.ctr.planCreated'));
      setCreateDialogOpen(false);
      setPlanForm({ project_id: '', name: '', start_date: '', end_date: '' });
      fetchPlans();
      loadPlan(response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create plan');
    }
  };

  const savePlan = async () => {
    if (!selectedPlan) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/manpower-plans/${selectedPlan.id}`, {
        entries: planEntries.map(e => ({
          employee_id: e.employee_id,
          role: e.role,
          date: e.date,
          working_hours: e.working_hours,
          travel_hours: e.travel_hours,
          notes: e.notes
        })),
        custom_non_working_days: customNonWorkingDays,
        employee_order: employeeOrder.map(e => ({
          id: e.id,
          role: e.role || '',
          isSpacer: e.isSpacer || false,
          name: e.name || ''
        }))
      });
      setOriginalEntries(JSON.parse(JSON.stringify(planEntries)));
      setOriginalCustomDays(JSON.parse(JSON.stringify(customNonWorkingDays)));
      setOriginalEmployeeOrder(JSON.parse(JSON.stringify(employeeOrder)));
      toast.success(t('auto.ctr.planSaved'));
    } catch (error) {
      console.error('Save error:', error);
      toast.error(t('auto.ctr.failedToSavePlan'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!selectedPlan || !window.confirm(t('auto.ctr.areYouSureYouWantTo2'))) return;
    
    try {
      await axios.delete(`${API}/manpower-plans/${selectedPlan.id}`);
      toast.success(t('auto.ctr.planDeleted'));
      setSelectedPlan(null);
      setOriginalEntries([]);
      setOriginalCustomDays([]);
      setEmployeeOrder([]);
      setOriginalEmployeeOrder([]);
      fetchPlans();
    } catch (error) {
      toast.error(t('auto.ctr.failedToDeletePlan'));
    }
  };

  const handleEditPlanDates = async () => {
    if (!selectedPlan || !editPlanForm.start_date || !editPlanForm.end_date) {
      toast.error(t('auto.ctr.pleaseFillInAllRequiredFields'));
      return;
    }

    if (editPlanForm.start_date > editPlanForm.end_date) {
      toast.error(t('auto.ctr.startDateCannotBeAfterEnd'));
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/manpower-plans/${selectedPlan.id}`, {
        name: editPlanForm.name || null,
        start_date: editPlanForm.start_date,
        end_date: editPlanForm.end_date
      });
      
      // Update the selected plan in state
      const updatedPlan = {
        ...selectedPlan,
        name: editPlanForm.name || selectedPlan.name,
        start_date: editPlanForm.start_date,
        end_date: editPlanForm.end_date
      };
      setSelectedPlan(updatedPlan);
      
      // Update plans list
      setPlans(plans.map(p => p.id === selectedPlan.id ? updatedPlan : p));
      
      setEditPlanDialogOpen(false);
      toast.success(t('auto.ctr.planDatesUpdatedSuccessfully'));
      
      // Refresh plan data to get updated holidays etc.
      fetchPlans();
    } catch (error) {
      console.error('Failed to update plan dates:', error);
      toast.error(t('auto.ctr.failedToUpdatePlanDates'));
    } finally {
      setSaving(false);
    }
  };

  // === TEMPLATE OPERATIONS ===
  
  const handleSaveAsTemplate = async () => {
    if (!selectedPlan || !templateForm.name.trim()) {
      toast.error(t('auto.ctr.pleaseEnterATemplateName'));
      return;
    }
    
    try {
      const templateData = {
        name: templateForm.name.trim(),
        description: templateForm.description.trim() || null,
        employees: employeeOrder.map(e => ({
          employee_id: e.id,
          role: e.role || '',
          is_spacer: e.isSpacer || false,
          name: e.name || ''
        })),
        default_working_hours: defaultWorkHours,
        default_travel_hours: defaultTravelHours
      };
      
      await axios.post(`${API}/manpower-templates`, templateData);
      toast.success(t('auto.ctr.templateSaved'));
      setSaveTemplateDialogOpen(false);
      setTemplateForm({ name: '', description: '' });
      
      const response = await axios.get(`${API}/manpower-templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error(t('auto.ctr.failedToSaveTemplate'));
    }
  };

  const handleLoadTemplate = (template) => {
    if (!selectedPlan) return;
    
    const newOrder = template.employees.map(te => ({
      id: te.employee_id,
      role: te.role,
      isSpacer: te.is_spacer,
      name: te.name
    }));
    
    setEmployeeOrder(newOrder);
    setDefaultWorkHours(template.default_working_hours || 10);
    setDefaultTravelHours(template.default_travel_hours || 0);
    setLoadTemplateDialogOpen(false);
    toast.success(`Template "${template.name}" loaded`);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm(t('auto.ctr.deleteThisTemplate'))) return;
    
    try {
      await axios.delete(`${API}/manpower-templates/${templateId}`);
      toast.success(t('auto.ctr.templateDeleted'));
      const response = await axios.get(`${API}/manpower-templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error(t('auto.ctr.failedToDeleteTemplate'));
    }
  };

  // === ENTRY OPERATIONS ===
  
  const isHoliday = useCallback((date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const plHoliday = polandHolidays.find(h => h.date === dateStr);
    const projHoliday = projectHolidays.find(h => h.date === dateStr);
    const customHoliday = customNonWorkingDays.find(d => {
      const customDateStr = typeof d === 'string' ? d.split('T')[0] : format(new Date(d), 'yyyy-MM-dd');
      return customDateStr === dateStr;
    });
    return { plHoliday, projHoliday, customHoliday };
  }, [polandHolidays, projectHolidays, customNonWorkingDays]);

  const getEmployeeUnavailability = useCallback((employeeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return availability.find(a => {
      const start = a.start_date.split('T')[0];
      const end = a.end_date.split('T')[0];
      return a.employee_id === employeeId && dateStr >= start && dateStr <= end;
    });
  }, [availability]);

  const getEntry = useCallback((employeeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return planEntries.find(e => 
      e.employee_id === employeeId && 
      (e.date.split('T')[0] === dateStr || format(new Date(e.date), 'yyyy-MM-dd') === dateStr)
    );
  }, [planEntries]);

  const updateEntry = (employeeId, date, field, value) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const existingIndex = planEntries.findIndex(e => 
      e.employee_id === employeeId && 
      (e.date.split('T')[0] === dateStr || format(new Date(e.date), 'yyyy-MM-dd') === dateStr)
    );

    if (existingIndex >= 0) {
      const updated = [...planEntries];
      updated[existingIndex] = { ...updated[existingIndex], [field]: value };
      setPlanEntries(updated);
    } else {
      const emp = planEmployees.find(e => e.id === employeeId);
      setPlanEntries([...planEntries, {
        id: `temp-${Date.now()}`,
        employee_id: employeeId,
        role: emp?.role || '',
        date: dateStr,
        working_hours: field === 'working_hours' ? value : 10,
        travel_hours: field === 'travel_hours' ? value : 0,
        notes: field === 'notes' ? value : ''
      }]);
    }
  };

  const setDayToDefault = (employeeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const existingIndex = planEntries.findIndex(e => 
      e.employee_id === employeeId && 
      (e.date.split('T')[0] === dateStr || format(new Date(e.date), 'yyyy-MM-dd') === dateStr)
    );

    if (existingIndex >= 0) {
      const updated = [...planEntries];
      updated[existingIndex] = { 
        ...updated[existingIndex], 
        working_hours: defaultWorkHours,
        travel_hours: defaultTravelHours 
      };
      setPlanEntries(updated);
    } else {
      const emp = planEmployees.find(e => e.id === employeeId);
      setPlanEntries([...planEntries, {
        id: `temp-${Date.now()}`,
        employee_id: employeeId,
        role: emp?.role || '',
        date: dateStr,
        working_hours: defaultWorkHours,
        travel_hours: defaultTravelHours,
        notes: ''
      }]);
    }
  };

  const setDayToZero = (employeeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const existingIndex = planEntries.findIndex(e => 
      e.employee_id === employeeId && 
      (e.date.split('T')[0] === dateStr || format(new Date(e.date), 'yyyy-MM-dd') === dateStr)
    );

    if (existingIndex >= 0) {
      const updated = [...planEntries];
      updated[existingIndex] = { 
        ...updated[existingIndex], 
        working_hours: 0,
        travel_hours: 0 
      };
      setPlanEntries(updated);
    } else {
      const emp = planEmployees.find(e => e.id === employeeId);
      setPlanEntries([...planEntries, {
        id: `temp-${Date.now()}`,
        employee_id: employeeId,
        role: emp?.role || '',
        date: dateStr,
        working_hours: 0,
        travel_hours: 0,
        notes: ''
      }]);
    }
  };

  // === EMPLOYEE OPERATIONS ===
  
  const handleAddEmployee = () => {
    if (!newEmployeeForm.employee_id || !newEmployeeForm.role) {
      toast.error(t('auto.ctr.pleaseSelectEmployeeAndRole'));
      return;
    }

    if (planEmployees.find(e => e.id === newEmployeeForm.employee_id && !e.isSpacer)) {
      toast.error(t('auto.ctr.employeeAlreadyInPlan'));
      return;
    }

    const emp = employees.find(e => e.id === newEmployeeForm.employee_id);
    const newEmpOrder = {
      id: newEmployeeForm.employee_id,
      name: emp?.name || 'Unknown',
      role: newEmployeeForm.role
    };

    const newEntries = allPlanDates.map(date => ({
      id: `temp-${Date.now()}-${Math.random()}`,
      employee_id: newEmployeeForm.employee_id,
      role: newEmployeeForm.role,
      date: format(date, 'yyyy-MM-dd'),
      working_hours: 10,
      travel_hours: 0,
      notes: ''
    }));

    setPlanEntries([...planEntries, ...newEntries]);
    setEmployeeOrder([...employeeOrder, newEmpOrder]);
    setAddEmployeeDialogOpen(false);
    setNewEmployeeForm({ employee_id: '', role: '' });
    toast.success(t('auto.ctr.employeeAddedToPlan'));
  };

  // Bulk add employees handler
  const handleBulkAddEmployees = () => {
    if (bulkSelectedEmployees.length === 0 || !bulkRole) {
      toast.error(t('auto.ctr.pleaseSelectEmployeesAndARole'));
      return;
    }

    // Filter out any employees already in plan
    const validEmployeeIds = bulkSelectedEmployees.filter(
      empId => !planEmployees.find(e => e.id === empId && !e.isSpacer)
    );

    if (validEmployeeIds.length === 0) {
      toast.error(t('auto.ctr.selectedEmployeesAreAlreadyInThe'));
      return;
    }

    // Create new employee order entries
    const newEmpOrders = validEmployeeIds.map(empId => {
      const emp = employees.find(e => e.id === empId);
      return {
        id: empId,
        name: emp?.name || 'Unknown',
        role: bulkRole
      };
    });

    // Create entries for all dates for all new employees
    const newEntries = [];
    validEmployeeIds.forEach(empId => {
      allPlanDates.forEach(date => {
        newEntries.push({
          id: `temp-${Date.now()}-${empId}-${Math.random()}`,
          employee_id: empId,
          role: bulkRole,
          date: format(date, 'yyyy-MM-dd'),
          working_hours: defaultWorkHours,
          travel_hours: defaultTravelHours,
          notes: ''
        });
      });
    });

    setPlanEntries([...planEntries, ...newEntries]);
    setEmployeeOrder([...employeeOrder, ...newEmpOrders]);
    setBulkAddDialogOpen(false);
    setBulkSelectedEmployees([]);
    setBulkRole('');
    toast.success(`${validEmployeeIds.length} employee${validEmployeeIds.length > 1 ? 's' : ''} added to plan`);
  };

  const openEditEmployeeDialog = (emp) => {
    setEditEmployeeForm({
      original_id: emp.id,
      employee_id: emp.id,
      role: emp.role
    });
    setEditEmployeeDialogOpen(true);
  };

  const handleUpdateEmployee = () => {
    if (!editEmployeeForm.employee_id || !editEmployeeForm.role) {
      toast.error(t('auto.ctr.pleaseSelectEmployeeAndRole'));
      return;
    }

    if (editEmployeeForm.employee_id !== editEmployeeForm.original_id && 
        planEmployees.find(e => e.id === editEmployeeForm.employee_id)) {
      toast.error(t('auto.ctr.employeeAlreadyInPlan'));
      return;
    }

    const updatedEntries = planEntries.map(e => {
      if (e.employee_id === editEmployeeForm.original_id) {
        return { ...e, employee_id: editEmployeeForm.employee_id, role: editEmployeeForm.role };
      }
      return e;
    });

    // Also update employee order
    const updatedOrder = employeeOrder.map(e => {
      if (e.id === editEmployeeForm.original_id) {
        const emp = employees.find(em => em.id === editEmployeeForm.employee_id);
        return { ...e, id: editEmployeeForm.employee_id, role: editEmployeeForm.role, name: emp?.name || e.name };
      }
      return e;
    });

    setPlanEntries(updatedEntries);
    setEmployeeOrder(updatedOrder);
    setEditEmployeeDialogOpen(false);
    toast.success(t('auto.ctr.employeeUpdated'));
  };

  const handleRemoveEmployee = (employeeId) => {
    if (!window.confirm(t('auto.ctr.removeThisEmployeeFromThePlan'))) return;
    setPlanEntries(planEntries.filter(e => e.employee_id !== employeeId));
    setEmployeeOrder(employeeOrder.filter(e => e.id !== employeeId));
  };

  const moveEmployee = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= employeeOrder.length) return;
    
    const newOrder = [...employeeOrder];
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    setEmployeeOrder(newOrder);
  };

  // Reorder employees via drag and drop
  const reorderEmployees = (sourceIndex, targetIndex) => {
    if (sourceIndex === targetIndex) return;
    
    const newOrder = [...employeeOrder];
    const [removed] = newOrder.splice(sourceIndex, 1);
    newOrder.splice(targetIndex, 0, removed);
    setEmployeeOrder(newOrder);
    toast.success(t('auto.ctr.employeeOrderUpdated'));
  };

  // Bulk hours assignment handler
  const handleBulkHoursAssignment = ({ employeeIds, startDate, endDate, workingHours, travelHours, overwriteMode, includeWeekends }) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    let updatedEntries = [...planEntries];
    let updatedCount = 0;
    let skippedCount = 0;

    // Iterate through each day in the range
    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      
      // Skip weekends unless includeWeekends is true
      if (isWeekend && !includeWeekends) {
        continue;
      }
      
      const dateStr = format(date, 'yyyy-MM-dd');
      
      // Process each selected employee
      for (const empId of employeeIds) {
        const existingIndex = updatedEntries.findIndex(
          e => e.employee_id === empId && e.date === dateStr
        );
        
        if (existingIndex >= 0) {
          // Entry exists
          if (overwriteMode === 'overwrite') {
            updatedEntries[existingIndex] = {
              ...updatedEntries[existingIndex],
              working_hours: workingHours,
              travel_hours: travelHours
            };
            updatedCount++;
          } else {
            // Skip mode - check if has non-zero hours
            const existing = updatedEntries[existingIndex];
            if (existing.working_hours > 0 || existing.travel_hours > 0) {
              skippedCount++;
            } else {
              // Entry exists but has no hours, update it
              updatedEntries[existingIndex] = {
                ...existing,
                working_hours: workingHours,
                travel_hours: travelHours
              };
              updatedCount++;
            }
          }
        } else {
          // Create new entry
          const orderItem = employeeOrder.find(e => e.id === empId || e.employee_id === empId);
          updatedEntries.push({
            employee_id: empId,
            date: dateStr,
            role: orderItem?.role || '',
            working_hours: workingHours,
            travel_hours: travelHours,
            availability_type: ''
          });
          updatedCount++;
        }
      }
    }
    
    setPlanEntries(updatedEntries);
    setBulkHoursDialogOpen(false);
    
    if (skippedCount > 0) {
      toast.success(`Updated ${updatedCount} entries, skipped ${skippedCount} existing entries`);
    } else {
      toast.success(`Updated ${updatedCount} entries`);
    }
  };

  const addSpacer = (afterIndex = employeeOrder.length - 1) => {
    const newOrder = [...employeeOrder];
    const spacer = { 
      id: `spacer-${Date.now()}`, 
      isSpacer: true, 
      name: '--- Spacer ---' 
    };
    newOrder.splice(afterIndex + 1, 0, spacer);
    setEmployeeOrder(newOrder);
  };

  const removeSpacer = (spacerId) => {
    setEmployeeOrder(employeeOrder.filter(e => e.id !== spacerId));
  };

  // === CUSTOM HOLIDAY OPERATIONS ===
  
  const handleAddCustomHoliday = () => {
    if (!customHolidayForm.date) {
      toast.error(t('auto.ctr.pleaseSelectADate'));
      return;
    }
    
    setCustomNonWorkingDays([...customNonWorkingDays, customHolidayForm.date]);
    setCustomHolidayDialogOpen(false);
    setCustomHolidayForm({ date: '' });
    toast.success(t('auto.ctr.customNonWorkingDayAdded'));
  };

  // === COPY/PASTE OPERATIONS ===
  
  const copyDay = (employeeId, date) => {
    const entry = getEntry(employeeId, date);
    if (entry) {
      setCopiedData({ type: 'day', data: { ...entry } });
      toast.success(t('auto.ctr.dayCopied'));
    }
  };

  const copyWeek = (employeeId, date) => {
    const weekStartDate = startOfWeek(date, { weekStartsOn: 1 });
    const weekEndDate = endOfWeek(date, { weekStartsOn: 1 });
    const weekDays = eachDayOfInterval({ start: weekStartDate, end: weekEndDate });
    const weekEntries = weekDays.map(d => getEntry(employeeId, d)).filter(Boolean);
    
    if (weekEntries.length > 0) {
      setCopiedData({ type: 'week', employeeId, weekStart: weekStartDate, data: weekEntries });
      toast.success(t('auto.ctr.weekCopied'));
    }
  };

  const pasteToDay = (employeeId, date) => {
    if (!copiedData || copiedData.type !== 'day') return;
    
    const dateStr = format(date, 'yyyy-MM-dd');
    const existingIndex = planEntries.findIndex(e => 
      e.employee_id === employeeId && 
      (e.date.split('T')[0] === dateStr || format(new Date(e.date), 'yyyy-MM-dd') === dateStr)
    );

    const newEntry = {
      ...copiedData.data,
      id: `temp-${Date.now()}`,
      employee_id: employeeId,
      date: dateStr
    };

    if (existingIndex >= 0) {
      const updated = [...planEntries];
      updated[existingIndex] = newEntry;
      setPlanEntries(updated);
    } else {
      setPlanEntries([...planEntries, newEntry]);
    }
    toast.success(t('auto.ctr.pasted'));
  };

  const pasteWeekToNext = (employeeId, targetDate) => {
    if (!copiedData || copiedData.type !== 'week') return;
    
    const targetWeekStart = startOfWeek(targetDate, { weekStartsOn: 1 });
    const targetDays = eachDayOfInterval({ 
      start: targetWeekStart, 
      end: endOfWeek(targetWeekStart, { weekStartsOn: 1 })
    });

    const newEntries = copiedData.data.map((entry, idx) => ({
      ...entry,
      id: `temp-${Date.now()}-${idx}`,
      employee_id: employeeId,
      date: format(targetDays[idx] || targetDays[targetDays.length - 1], 'yyyy-MM-dd')
    }));

    const filtered = planEntries.filter(e => {
      if (e.employee_id !== employeeId) return true;
      const entryDate = new Date(e.date);
      return entryDate < targetWeekStart || entryDate > endOfWeek(targetWeekStart, { weekStartsOn: 1 });
    });

    setPlanEntries([...filtered, ...newEntries]);
    toast.success(t('auto.ctr.weekPasted'));
  };

  const quickCopyWeekToNext = () => {
    if (!viewStartDate || !selectedPlan || planEmployees.length === 0) {
      toast.error(t('auto.ctr.noDataToCopy'));
      return;
    }

    const currentWeekStart = startOfWeek(viewStartDate, { weekStartsOn: 1 });
    const currentWeekEnd = endOfWeek(viewStartDate, { weekStartsOn: 1 });
    const nextWeekStart = addWeeks(currentWeekStart, 1);
    const nextWeekEnd = endOfWeek(nextWeekStart, { weekStartsOn: 1 });
    
    const planEnd = new Date(selectedPlan.end_date);
    if (nextWeekStart > planEnd) {
      toast.error(t('auto.ctr.cannotCopyNextWeekIsOutside'));
      return;
    }

    const currentWeekDays = eachDayOfInterval({ start: currentWeekStart, end: currentWeekEnd });
    const nextWeekDays = eachDayOfInterval({ start: nextWeekStart, end: nextWeekEnd });

    const currentWeekEntries = [];
    planEmployees.forEach(emp => {
      if (emp.isSpacer) return;
      currentWeekDays.forEach((day, dayIndex) => {
        const entry = getEntry(emp.id, day);
        if (entry) {
          currentWeekEntries.push({
            ...entry,
            _dayIndex: dayIndex,
            _originalEmployeeId: emp.id
          });
        }
      });
    });

    if (currentWeekEntries.length === 0) {
      toast.error(t('auto.ctr.noEntriesInCurrentWeekTo'));
      return;
    }

    const newEntries = currentWeekEntries.map((entry, idx) => {
      const targetDay = nextWeekDays[entry._dayIndex];
      if (!targetDay || targetDay > planEnd) return null;
      
      return {
        ...entry,
        id: `temp-${Date.now()}-${idx}`,
        employee_id: entry._originalEmployeeId,
        date: format(targetDay, 'yyyy-MM-dd'),
        _dayIndex: undefined,
        _originalEmployeeId: undefined
      };
    }).filter(Boolean);

    const filtered = planEntries.filter(e => {
      const entryDate = new Date(e.date);
      return entryDate < nextWeekStart || entryDate > nextWeekEnd;
    });

    setPlanEntries([...filtered, ...newEntries]);
    setViewStartDate(nextWeekStart);
    toast.success(`Copied ${newEntries.length} entries to Week ${getWeekNumber(nextWeekStart)}`);
  };

  const copyWeekToMultipleWeeks = () => {
    if (!viewStartDate || !selectedPlan || planEmployees.length === 0) {
      toast.error(t('auto.ctr.noDataToCopy'));
      return;
    }

    const targetWeeksCount = copyMultiWeeksForm.targetWeeks;
    if (targetWeeksCount < 1 || targetWeeksCount > maxCopyWeeks) {
      toast.error(`Please select between 1 and ${maxCopyWeeks} weeks`);
      return;
    }

    const currentWeekStart = startOfWeek(viewStartDate, { weekStartsOn: 1 });
    const currentWeekEnd = endOfWeek(viewStartDate, { weekStartsOn: 1 });
    const currentWeekDays = eachDayOfInterval({ start: currentWeekStart, end: currentWeekEnd });
    const planEnd = new Date(selectedPlan.end_date);

    const currentWeekEntries = [];
    planEmployees.forEach(emp => {
      if (emp.isSpacer) return;
      currentWeekDays.forEach((day, dayIndex) => {
        const entry = getEntry(emp.id, day);
        if (entry) {
          currentWeekEntries.push({
            ...entry,
            _dayIndex: dayIndex,
            _originalEmployeeId: emp.id
          });
        }
      });
    });

    if (currentWeekEntries.length === 0) {
      toast.error(t('auto.ctr.noEntriesInCurrentWeekTo'));
      setCopyMultiWeeksDialogOpen(false);
      return;
    }

    const allNewEntries = [];
    const targetWeekRanges = [];

    for (let weekOffset = 1; weekOffset <= targetWeeksCount; weekOffset++) {
      const targetWeekStart = addWeeks(currentWeekStart, weekOffset);
      const targetWeekEnd = endOfWeek(targetWeekStart, { weekStartsOn: 1 });
      
      if (targetWeekStart > planEnd) break;
      
      targetWeekRanges.push({ start: targetWeekStart, end: targetWeekEnd });
      const targetWeekDays = eachDayOfInterval({ start: targetWeekStart, end: targetWeekEnd });

      currentWeekEntries.forEach((entry, idx) => {
        const targetDay = targetWeekDays[entry._dayIndex];
        if (!targetDay || targetDay > planEnd) return;
        
        allNewEntries.push({
          ...entry,
          id: `temp-${Date.now()}-${weekOffset}-${idx}`,
          employee_id: entry._originalEmployeeId,
          date: format(targetDay, 'yyyy-MM-dd'),
          _dayIndex: undefined,
          _originalEmployeeId: undefined
        });
      });
    }

    const filtered = planEntries.filter(e => {
      const entryDate = new Date(e.date);
      return !targetWeekRanges.some(range => entryDate >= range.start && entryDate <= range.end);
    });

    setPlanEntries([...filtered, ...allNewEntries]);
    setCopyMultiWeeksDialogOpen(false);
    
    if (targetWeekRanges.length > 0) {
      setViewStartDate(targetWeekRanges[0].start);
    }
    
    const weekNumbers = targetWeekRanges.map(r => getWeekNumber(r.start)).join(', ');
    toast.success(`Copied ${allNewEntries.length} entries to Week(s) ${weekNumbers}`);
  };

  // === NAVIGATION OPERATIONS ===
  
  const navigateView = (direction) => {
    if (!selectedPlan || !viewStartDate) return;
    const planStart = new Date(selectedPlan.start_date);
    const planEnd = new Date(selectedPlan.end_date);
    
    const newDate = direction > 0 ? addWeeks(viewStartDate, 1) : subWeeks(viewStartDate, 1);
    
    if (newDate < planStart) {
      setViewStartDate(planStart);
    } else if (newDate > planEnd) {
      return;
    } else {
      setViewStartDate(newDate);
    }
  };

  const goToPlanStart = () => {
    if (selectedPlan) {
      setViewStartDate(new Date(selectedPlan.start_date));
    }
  };

  const goToPlanEnd = () => {
    if (selectedPlan) {
      setViewStartDate(subWeeks(new Date(selectedPlan.end_date), 3));
    }
  };

  const handleWeekDropdownChange = (value) => {
    setViewStartDate(new Date(value));
  };

  const handleDateSelect = (date) => {
    if (date && selectedPlan) {
      const planStart = new Date(selectedPlan.start_date);
      const planEnd = new Date(selectedPlan.end_date);
      
      if (date < planStart) {
        setViewStartDate(planStart);
      } else if (date > planEnd) {
        setViewStartDate(subWeeks(planEnd, 3));
      } else {
        setViewStartDate(date);
      }
      setDatePickerOpen(false);
    }
  };

  // === EXPORT OPERATIONS ===
  
  const handleExportPlan = async () => {
    if (!selectedPlan || planEmployees.length === 0) {
      toast.error(t('auto.ctr.noPlanDataToExport'));
      return;
    }

    setExportLoading(true);
    try {
      const startDate = exportRange.exportWholePlan ? selectedPlan.start_date : exportRange.startDate;
      const endDate = exportRange.exportWholePlan ? selectedPlan.end_date : exportRange.endDate;
      
      await exportProjectPlan({
        plan: selectedPlan,
        planEntries,
        planEmployees,
        polandHolidays,
        projectHolidays,
        customNonWorkingDays,
        startDate,
        endDate,
        employees,
        companyInfo
      });
      
      toast.success(t('auto.ctr.excelFileDownloaded'));
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error(t('auto.ctr.failedToExport'));
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportPlanPDF = async () => {
    if (!selectedPlan || planEmployees.length === 0) {
      toast.error(t('auto.ctr.noPlanDataToExport'));
      return;
    }

    setExportLoading(true);
    try {
      const startDate = exportRange.exportWholePlan ? selectedPlan.start_date : exportRange.startDate;
      const endDate = exportRange.exportWholePlan ? selectedPlan.end_date : exportRange.endDate;
      
      await exportProjectPlanPDF({
        plan: selectedPlan,
        planEntries,
        planEmployees,
        polandHolidays,
        projectHolidays,
        customNonWorkingDays,
        startDate,
        endDate,
        employees,
        companyInfo
      });
      
      toast.success(t('auto.ctr.pdfFileDownloaded'));
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error(t('auto.ctr.failedToExportPdf'));
    } finally {
      setExportLoading(false);
    }
  };

  const openExportDialog = () => {
    if (!selectedPlan) {
      toast.error(t('auto.ctr.pleaseSelectAPlanFirst'));
      return;
    }
    setExportRange({
      startDate: selectedPlan.start_date.split('T')[0],
      endDate: selectedPlan.end_date.split('T')[0],
      exportWholePlan: true
    });
    setExportDialogOpen(true);
  };

  // === RENDER ===
  
  if (loading && !selectedPlan) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Plan Selection */}
      <PlanSelector
        plans={plans}
        selectedPlan={selectedPlan}
        loading={loading}
        projects={projects}
        onSelectPlan={handlePlanSelect}
        onCreateNew={() => setCreateDialogOpen(true)}
      />

      {/* Selected Plan Editor */}
      {selectedPlan && (
        <>
          {/* Unsaved Changes Warning */}
          {hasUnsavedChanges && (
            <UnsavedChangesWarning saving={saving} onSave={savePlan} />
          )}

          {/* Plan Header */}
          <PlanHeader
            plan={selectedPlan}
            hasUnsavedChanges={hasUnsavedChanges}
            saving={saving}
            defaultWorkHours={defaultWorkHours}
            defaultTravelHours={defaultTravelHours}
            onDefaultWorkHoursChange={setDefaultWorkHours}
            onDefaultTravelHoursChange={setDefaultTravelHours}
            onAddEmployee={() => setAddEmployeeDialogOpen(true)}
            onBulkAddEmployees={() => setBulkAddDialogOpen(true)}
            onBulkHours={() => setBulkHoursDialogOpen(true)}
            onAddHoliday={() => setCustomHolidayDialogOpen(true)}
            onSaveTemplate={() => setSaveTemplateDialogOpen(true)}
            onLoadTemplate={() => setLoadTemplateDialogOpen(true)}
            onEditPlan={() => setEditPlanDialogOpen(true)}
            onSave={savePlan}
            onDelete={handleDeletePlan}
          />

          {/* Navigation Controls */}
          <PlanNavigation
            weekNumber={viewStartDate ? getWeekNumber(viewStartDate) : 0}
            viewStartDate={viewStartDate}
            viewEndDate={visibleDates.length > 0 ? visibleDates[visibleDates.length - 1] : viewStartDate}
            planStartDate={new Date(selectedPlan.start_date)}
            planEndDate={new Date(selectedPlan.end_date)}
            weekOptions={weekOptions}
            currentWeekValue={currentWeekValue}
            datePickerOpen={datePickerOpen}
            setDatePickerOpen={setDatePickerOpen}
            onNavigate={navigateView}
            onGoToPlanStart={goToPlanStart}
            onGoToPlanEnd={goToPlanEnd}
            onGoToCurrentWeek={() => setViewStartDate(startOfWeek(new Date(), { weekStartsOn: 1 }))}
            onWeekDropdownChange={handleWeekDropdownChange}
            onDateSelect={handleDateSelect}
            onQuickCopyWeek={quickCopyWeekToNext}
            onCopyMultiWeeks={() => { setCopyMultiWeeksForm({ targetWeeks: 1 }); setCopyMultiWeeksDialogOpen(true); }}
            onExport={openExportDialog}
          />

          {/* Employee Grid */}
          <EmployeeGrid
            planEmployees={planEmployees}
            employees={employees}
            dates={visibleDates}
            copiedData={copiedData}
            defaultWorkHours={defaultWorkHours}
            defaultTravelHours={defaultTravelHours}
            isHoliday={isHoliday}
            getEmployeeUnavailability={getEmployeeUnavailability}
            getEntry={getEntry}
            getAvailabilityTypeInfo={getAvailabilityTypeInfo}
            onMoveEmployee={moveEmployee}
            onReorderEmployees={reorderEmployees}
            onEditEmployee={openEditEmployeeDialog}
            onRemoveEmployee={handleRemoveEmployee}
            onAddSpacer={() => addSpacer()}
            onRemoveSpacer={removeSpacer}
            onSetDayToDefault={setDayToDefault}
            onSetDayToZero={setDayToZero}
            onUpdateEntry={updateEntry}
            onCopyDay={copyDay}
            onPasteDay={pasteToDay}
            onCopyWeek={copyWeek}
            onPasteWeek={pasteWeekToNext}
          />

          {/* Legend */}
          <Card>
            <CardContent className="py-3">
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="font-medium">{t('auto.ctr.legend')}</span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 bg-red-50 border border-red-200 rounded"></span>
                  <span>{t('auto.ctr.holidayWeekend')}</span>
                </span>
                {AVAILABILITY_TYPES.map((type) => (
                  <span key={type.value} className="flex items-center gap-1">
                    <Badge className={type.color + ' text-xs'}>{type.shortLabel}</Badge>
                    <span className="text-gray-600">{type.label}</span>
                  </span>
                ))}
                <span className="flex items-center gap-1">
                  🇵🇱 <span>{t('auto.ctr.polishHoliday')}</span>
                </span>
                <span className="flex items-center gap-1">
                  🏴 <span>{t('auto.ctr.projectCountryHoliday')}</span>
                </span>
                <span className="flex items-center gap-1">
                  ⭐ <span>{t('auto.ctr.customNonWorkingDay')}</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-6 h-4 bg-blue-50 border border-blue-200 rounded text-xs flex items-center justify-center">T</span>
                  <span>{t('auto.ctr.travelHours')}</span>
                </span>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* All Dialogs */}
      <CreatePlanDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        form={planForm}
        setForm={setPlanForm}
        projects={projects}
        loading={loading}
        onSubmit={handleCreatePlan}
      />

      <AddEmployeeDialog
        open={addEmployeeDialogOpen}
        onOpenChange={setAddEmployeeDialogOpen}
        form={newEmployeeForm}
        setForm={setNewEmployeeForm}
        employees={employees}
        projectRoles={projectRoles}
        existingEmployeeIds={planEmployees.filter(e => !e.isSpacer).map(e => e.id)}
        onSubmit={handleAddEmployee}
      />

      <BulkAddEmployeeDialog
        open={bulkAddDialogOpen}
        onOpenChange={setBulkAddDialogOpen}
        employees={employees}
        projectRoles={projectRoles}
        existingEmployeeIds={planEmployees.filter(e => !e.isSpacer).map(e => e.id)}
        selectedEmployees={bulkSelectedEmployees}
        setSelectedEmployees={setBulkSelectedEmployees}
        bulkRole={bulkRole}
        setBulkRole={setBulkRole}
        onSubmit={handleBulkAddEmployees}
      />

      <EditEmployeeDialog
        open={editEmployeeDialogOpen}
        onOpenChange={setEditEmployeeDialogOpen}
        form={editEmployeeForm}
        setForm={setEditEmployeeForm}
        employees={employees}
        projectRoles={projectRoles}
        existingEmployeeIds={planEmployees.filter(e => !e.isSpacer).map(e => e.id)}
        onSubmit={handleUpdateEmployee}
      />

      <CustomHolidayDialog
        open={customHolidayDialogOpen}
        onOpenChange={setCustomHolidayDialogOpen}
        form={customHolidayForm}
        setForm={setCustomHolidayForm}
        onSubmit={handleAddCustomHoliday}
      />

      <UnsavedChangesDialog
        open={unsavedChangesDialogOpen}
        onOpenChange={setUnsavedChangesDialogOpen}
        onDiscard={handleDiscardChanges}
        onSave={handleSaveAndContinue}
      />

      <CopyMultiWeeksDialog
        open={copyMultiWeeksDialogOpen}
        onOpenChange={setCopyMultiWeeksDialogOpen}
        weekNumber={viewStartDate ? getWeekNumber(viewStartDate) : 0}
        maxWeeks={maxCopyWeeks}
        weeksToFill={copyMultiWeeksForm.targetWeeks}
        setWeeksToFill={(n) => setCopyMultiWeeksForm({ targetWeeks: n })}
        onSubmit={copyWeekToMultipleWeeks}
      />

      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        exportRange={exportRange}
        setExportRange={setExportRange}
        planStart={selectedPlan ? new Date(selectedPlan.start_date) : new Date()}
        planEnd={selectedPlan ? new Date(selectedPlan.end_date) : new Date()}
        loading={exportLoading}
        onSubmit={handleExportPlan}
        onSubmitPDF={handleExportPlanPDF}
      />

      <SaveTemplateDialog
        open={saveTemplateDialogOpen}
        onOpenChange={setSaveTemplateDialogOpen}
        form={templateForm}
        setForm={setTemplateForm}
        employeeOrder={employeeOrder}
        defaultWorkHours={defaultWorkHours}
        defaultTravelHours={defaultTravelHours}
        onSubmit={handleSaveAsTemplate}
      />

      <LoadTemplateDialog
        open={loadTemplateDialogOpen}
        onOpenChange={setLoadTemplateDialogOpen}
        templates={templates}
        onLoad={handleLoadTemplate}
        onDelete={handleDeleteTemplate}
      />

      <BulkHoursDialog
        open={bulkHoursDialogOpen}
        onOpenChange={setBulkHoursDialogOpen}
        employeeOrder={employeeOrder}
        employees={employees}
        planStartDate={selectedPlan ? format(new Date(selectedPlan.start_date), 'yyyy-MM-dd') : ''}
        planEndDate={selectedPlan ? format(new Date(selectedPlan.end_date), 'yyyy-MM-dd') : ''}
        onSubmit={handleBulkHoursAssignment}
      />

      <EditPlanDialog
        open={editPlanDialogOpen}
        onOpenChange={setEditPlanDialogOpen}
        plan={selectedPlan}
        form={editPlanForm}
        setForm={setEditPlanForm}
        loading={saving}
        onSubmit={handleEditPlanDates}
      />
    </div>
  );
};

export default ProjectPlanningTab;
